import { useState, useCallback, useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import { map, isEmpty } from "lodash"
import { List, ListItemButton, ListItemText } from "@mui/material"
import { CategoryShape } from "./helpers/prop-types"
import ExpandIcon from "./expand-icon"
import MetricsSubcategory from "./metrics-subcategory"

const reduceSelectedMetricsToObject = (metrics = []) => {
  return metrics.reduce((acc, metric) => {
    acc[metric] = true
    return acc
  }, {})
}

export default function MetricsItems({ category, disableOptions, selectedMetrics, setSelectedMetrics, includeMetricTypes, metricsSearchValue, onCategoryExpanded, onCategoryCollapsed, onMetricToggled }) {
  const [openSections, setOpenSections] = useState({})
  const [collapsedSearchSections, setCollapsedSearchSections] = useState({})

  const selectedMetricsObject = useMemo(() => reduceSelectedMetricsToObject(selectedMetrics), [selectedMetrics])

  useEffect(() => {
    if (metricsSearchValue === "" && !isEmpty(collapsedSearchSections)) {
      setCollapsedSearchSections({})
    }
  }, [metricsSearchValue, collapsedSearchSections])

  const getIsSectionOpen = useCallback((key) => {
    if (metricsSearchValue) {
      return !collapsedSearchSections?.[key]
    }
    return !!openSections?.[key]
  }, [openSections, collapsedSearchSections, metricsSearchValue])

  const toggleSection = useCallback((key) => {
    const isCurrentlyOpen = getIsSectionOpen(key)
    if (isCurrentlyOpen) {
      onCategoryCollapsed?.(key)
    } else {
      onCategoryExpanded?.(key)
    }

    // If we are using the search, then found items are auto-expanded,
    // so we need store the open/collapsed state of search results in a separate object
    if (metricsSearchValue) {
      setCollapsedSearchSections({
        ...collapsedSearchSections,
        [key]: !collapsedSearchSections[key],
      })
    } else {
      setOpenSections({
        ...openSections,
        [key]: !openSections[key],
      })
    }
  }, [getIsSectionOpen, metricsSearchValue, onCategoryCollapsed, onCategoryExpanded, collapsedSearchSections, openSections])

  const getIsSelected = useCallback((selector) => {
    return !!selectedMetricsObject[selector]
  }, [selectedMetricsObject])

  const {
    shouldShowItems,
    itemsFound,
    itemsSelected,
    totalItems,
    items,
  } = category

  const label = category.label || category.category
  const isSectionOpen = getIsSectionOpen(category.selector)
  const itemCountLabel = metricsSearchValue
    ? `(${itemsFound} found)`
    : `(${itemsSelected} / ${totalItems})`

  const toggleSelectedMetricColumn = useCallback((selector) => {
    const metricIndex = selectedMetrics.findIndex(metric => metric === selector)
    const isCurrentlySelected = metricIndex !== -1

    onMetricToggled?.(selector, !isCurrentlySelected)

    if (isCurrentlySelected) {
      setSelectedMetrics(prev => prev.filter(m => m !== selector))
    } else if (!disableOptions) {
      setSelectedMetrics(prev => [...prev, selector])
    }
  }, [setSelectedMetrics, disableOptions, selectedMetrics, onMetricToggled])

  const handleClick = useCallback(() => {
    const handler = itemsFound ? toggleSection : toggleSelectedMetricColumn
    handler(category.selector)
  }, [itemsFound, toggleSection, toggleSelectedMetricColumn, category.selector])

  if (!shouldShowItems) {
    return null
  }

  return <List disablePadding className="metrics-category">
    <ListItemButton
      classes={{ root: "list-subcategory list-item" }}
      onClick={handleClick}
    >
      <ListItemText>
        {label} <span className="items-count">{itemCountLabel}</span>
      </ListItemText>
      <ExpandIcon
        isOpen={isSectionOpen}
        isHidden={!itemsFound}
      />
    </ListItemButton>

    {items && isSectionOpen && <List className="searchable-list" disablePadding>
      {map(items, item => <MetricsSubcategory
        key={`${category.type}.${item.id}`}
        categoryType={category.type}
        subcategory={item}
        toggleSection={toggleSection}
        toggleSelectedMetricColumn={toggleSelectedMetricColumn}
        disableOptions={disableOptions}
        openSections={openSections}
        selectedMetrics={selectedMetrics}
        includeMetricTypes={includeMetricTypes}
        metricsSearchValue={metricsSearchValue}
        getIsSectionOpen={getIsSectionOpen}
        getIsSelected={getIsSelected}
        onMetricToggled={onMetricToggled}
      />)}
    </List>}
  </List>
}

MetricsItems.propTypes = {
  category: CategoryShape.isRequired,
  disableOptions: PropTypes.bool.isRequired,
  selectedMetrics: PropTypes.array.isRequired,
  setSelectedMetrics: PropTypes.func.isRequired,
  includeMetricTypes: PropTypes.bool,
  metricsSearchValue: PropTypes.string.isRequired,
  onCategoryExpanded: PropTypes.func,
  onCategoryCollapsed: PropTypes.func,
  onMetricToggled: PropTypes.func,
}
